import { useRef, useState } from 'react';

// Paginated single-card carousel — transform-translateX based. Pointer
// drag (touch + mouse) swipes through cards; arrows / keyboard navigate.
// Tap a centered card to toggle its pick state.
export function PickCarousel({
  poolSize,
  requiredPicks,
  pickedSlots,
  onTogglePick,
}: {
  poolSize: number;
  requiredPicks: number;
  pickedSlots: number[];
  onTogglePick: (slot: number) => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [dragDX, setDragDX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);
  // Distinguishes "click after drag" from a clean tap on a card.
  const justDragged = useRef(false);
  // Velocity tracking for momentum-based fling. Sampled on pointermove
  // via exponential smoothing so a single jitter doesn't dominate.
  const velocity = useRef(0); // px per ms (positive = moving right)
  const lastSampleTime = useRef(0);
  const lastSampleX = useRef(0);

  const clamp = (n: number) => Math.max(0, Math.min(poolSize - 1, n));

  const onPointerDown = (e: React.PointerEvent) => {
    // Only react to primary button on mouse; touch + pen have no button.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, moved: false };
    setIsDragging(true);
    velocity.current = 0;
    lastSampleTime.current = performance.now();
    lastSampleX.current = e.clientX;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    // Lock to horizontal drag only after a small threshold so vertical
    // page scroll still works.
    if (!dragRef.current.moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      dragRef.current.moved = true;
    }
    if (dragRef.current.moved) {
      setDragDX(dx);
      // Sample instantaneous velocity (px/ms) with exponential smoothing.
      const now = performance.now();
      const dt = now - lastSampleTime.current;
      if (dt > 0) {
        const instV = (e.clientX - lastSampleX.current) / dt;
        velocity.current = velocity.current * 0.6 + instV * 0.4;
      }
      lastSampleTime.current = now;
      lastSampleX.current = e.clientX;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const moved = dragRef.current.moved;
    if (moved) {
      justDragged.current = true;
      queueMicrotask(() => { justDragged.current = false; });
      const finalDx = e.clientX - dragRef.current.startX;
      const idleMs = performance.now() - lastSampleTime.current;
      const v = idleMs > 80 ? 0 : velocity.current;
      const projected = finalDx + v * 220;
      // One card step = stridePct% of card width = 13% of container width.
      const containerW = containerRef.current?.offsetWidth || 1;
      const stride = containerW * 0.26 * 0.5;
      let step = -Math.round(projected / stride);
      if (step === 0 && Math.abs(finalDx) > 40) step = finalDx < 0 ? 1 : -1;
      setActiveIdx((i) => clamp(i + step));
    }
    dragRef.current = null;
    setDragDX(0);
    setIsDragging(false);
  };

  // Keyboard ← → anywhere on the pager.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActiveIdx((i) => clamp(i - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setActiveIdx((i) => clamp(i + 1));
    }
  };

  // Stride between adjacent card centers, expressed as % of card width
  // (so translateX(50%) === half a card). Half-card stride gives a visible
  // gap between centers while keeping enough overlap for the fan look.
  const stridePct = 50;
  // Card width as fraction of container (must mirror .pick-pager-card width).
  const cardWidthFrac = 0.26;

  return (
    <div className="pick-pager" ref={containerRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="pick-pager-arrow left"
        onClick={() => setActiveIdx((i) => clamp(i - 1))}
        disabled={activeIdx === 0}
        aria-label="上一張"
      >
        ‹
      </button>
      <button
        type="button"
        className="pick-pager-arrow right"
        onClick={() => setActiveIdx((i) => clamp(i + 1))}
        disabled={activeIdx === poolSize - 1}
        aria-label="下一張"
      >
        ›
      </button>
      <div
        className="pick-pager-viewport"
        role="listbox"
        aria-multiselectable="true"
        aria-label={`從 ${poolSize} 張牌中選 ${requiredPicks} 張`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ touchAction: 'pan-y' }}
      >
        {Array.from({ length: poolSize }, (_, slot) => {
          const distFromActive = slot - activeIdx;
          // Render a buffer of ±6 so enter/exit transitions are smooth even
          // on fast swipes. Beyond that, hide completely.
          const inBuffer = Math.abs(distFromActive) <= 6;

          // Smooth drag-adjusted distance for scale/opacity interpolation
          // mid-gesture. stridePx is the px equivalent of one card-step,
          // i.e. stridePct% of card width.
          const containerW = containerRef.current?.offsetWidth ?? 0;
          const cardWidthPx = containerW * cardWidthFrac;
          const stridePx = (cardWidthPx * stridePct) / 100;
          const dragFrac = stridePx > 0 ? dragDX / stridePx : 0;
          const effDist = distFromActive + dragFrac;
          const absEff = Math.abs(effDist);

          const scale = Math.max(0.4, 1 - absEff * 0.15);
          const opacity = inBuffer ? Math.max(0.05, 1 - absEff * 0.28) : 0;
          const zIndex = Math.max(0, Math.round(100 - absEff * 10));
          const offsetPct = distFromActive * stridePct;

          const pickIndex = pickedSlots.indexOf(slot);
          const isPicked = pickIndex >= 0;
          const isFull = pickedSlots.length >= requiredPicks && !isPicked;
          const isCenter = slot === activeIdx;

          return (
            <button
              key={slot}
              type="button"
              data-pick-index={isPicked ? pickIndex + 1 : ''}
              tabIndex={isCenter ? 0 : -1}
              aria-hidden={!inBuffer || undefined}
              onClick={() => {
                if (justDragged.current) return;
                // Off-center tap → navigate to that card; only the centered
                // card toggles its pick state.
                if (!isCenter) {
                  setActiveIdx(slot);
                  return;
                }
                if (isFull) return;
                onTogglePick(slot);
              }}
              role="option"
              aria-selected={isPicked}
              aria-label={isPicked ? `已選為第 ${pickIndex + 1} 張` : `第 ${slot + 1} 張`}
              className={`pick-pager-card ${isPicked ? 'picked' : ''}`}
              style={{
                transform: `translate(-50%, -50%) translateX(calc(${offsetPct}% + ${dragDX}px)) scale(${scale})`,
                opacity,
                zIndex,
                pointerEvents: inBuffer ? 'auto' : 'none',
                transition: isDragging
                  ? 'none'
                  : 'transform .35s cubic-bezier(.4, 1.2, .5, 1), opacity .25s, border-color .25s, box-shadow .25s, color .25s',
              }}
            >
              <svg
                className="pick-pager-card-glyph"
                viewBox="0 0 64 64"
                aria-hidden="true"
              >
                <circle cx="32" cy="32" r="14" fill="none" stroke="currentColor" strokeWidth="1.2" />
                <path
                  d="M32 10 L32 18 M32 46 L32 54 M10 32 L18 32 M46 32 L54 32"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <circle cx="32" cy="32" r="2.5" fill="currentColor" />
              </svg>
            </button>
          );
        })}
      </div>
      <div className="pick-pager-meta">
        <span className="pick-pager-counter">
          {activeIdx + 1} / {poolSize}
        </span>
      </div>
    </div>
  );
}
